import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { I18nextProvider } from "react-i18next";
import i18n from "./lib/i18n";
import { useEffect, lazy, Suspense } from "react";
import { getOrCreateAnonUserId } from "@/utils/user";
import { RecaptchaProvider } from "@/contexts/RecaptchaContext";
import { AuthProvider } from "@/contexts/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useAnalytics } from "@/hooks/useAnalytics";

// ✅ Critical routes - keep in main bundle for first paint
import Home from "@/pages/home";

// ✅ Non-critical routes - lazy load to reduce main bundle
const Features = lazy(() => import("@/pages/features"));
const Pricing = lazy(() => import("@/pages/pricing"));
const About = lazy(() => import("@/pages/about"));
const Contact = lazy(() => import("@/pages/contact"));
const Services = lazy(() => import("@/pages/services"));
const Support = lazy(() => import("@/pages/support"));
const TermsOfService = lazy(() => import("@/pages/terms"));
const PrivacyPolicy = lazy(() => import("@/pages/privacy"));
const RefundPolicy = lazy(() => import("@/pages/refund"));
const PaymentSuccess = lazy(() => import("@/pages/payment-success"));
const PaymentCancelled = lazy(() => import("@/pages/payment-cancelled"));
const AdminPage = lazy(() => import("@/pages/admin"));
const VideoDetailPage = lazy(() => import("@/pages/video-detail"));
const NotFound = lazy(() => import("@/pages/not-found"));

// ✅ Suspense wrapper for lazy components
function withSuspense(Component: React.ComponentType<any>) {
  return (props: any) => (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    }>
      <Component {...props} />
    </Suspense>
  );
}

function Router() {
  return (
    <Switch>
      {/* Critical routes - no suspense needed */}
      <Route path="/" component={Home} />
      
      {/* English routes (no language prefix) - lazy load with suspense */}
      <Route path="/features" component={withSuspense(Features)} />
      <Route path="/pricing" component={withSuspense(Pricing)} />
      <Route path="/about" component={withSuspense(About)} />
      <Route path="/contact" component={withSuspense(Contact)} />
      <Route path="/services" component={withSuspense(Services)} />
      <Route path="/support" component={withSuspense(Support)} />
      <Route path="/terms" component={withSuspense(TermsOfService)} />
      <Route path="/privacy" component={withSuspense(PrivacyPolicy)} />
      <Route path="/refund" component={withSuspense(RefundPolicy)} />
      <Route path="/payment-success" component={withSuspense(PaymentSuccess)} />
      <Route path="/payment-cancelled" component={withSuspense(PaymentCancelled)} />
      <Route path="/admin01101990" component={withSuspense(AdminPage)} />
      <Route path="/admin/video-detail" component={withSuspense(VideoDetailPage)} />
      
      {/* Language-specific routes - must come after English routes */}
      <Route path="/:lang" component={Home} />
      <Route path="/:lang/features" component={withSuspense(Features)} />
      <Route path="/:lang/pricing" component={withSuspense(Pricing)} />
      <Route path="/:lang/about" component={withSuspense(About)} />
      <Route path="/:lang/contact" component={withSuspense(Contact)} />
      <Route path="/:lang/services" component={withSuspense(Services)} />
      <Route path="/:lang/support" component={withSuspense(Support)} />
      <Route path="/:lang/terms" component={withSuspense(TermsOfService)} />
      <Route path="/:lang/privacy" component={withSuspense(PrivacyPolicy)} />
      <Route path="/:lang/refund" component={withSuspense(RefundPolicy)} />
      
      {/* 404 route */}
      <Route component={withSuspense(NotFound)} />
    </Switch>
  );
}

function App() {
  // Initialize analytics
  useAnalytics();
  
  useEffect(() => {
    // Ensure an anonymous user id exists on first load
    getOrCreateAnonUserId();
  }, []);
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <I18nextProvider i18n={i18n}>
          <TooltipProvider>
            <RecaptchaProvider>
              <AuthProvider>
                <Toaster />
                <Router />
              </AuthProvider>
            </RecaptchaProvider>
          </TooltipProvider>
        </I18nextProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
